import * as Sentry from "@sentry/react";
import { BrowserTracing } from "@sentry/tracing";

export const initSentry = () => {
  Sentry.init({
    dsn: process.env.BIG_FLIP_SENTRY_DSN,
    release: process.env.BIG_FLIP_GIT_SHA + "-" + process.env.NODE_ENV, // CommitId-staging, e.g asdqwe-staging or asdqwe-production
    environment: process.env.NODE_ENV,
    integrations: [new BrowserTracing()]
  });
};

// holds the latest sentry user id.
// This is to prevent us sending the same user if user has been set.
let currentUserId;

export const setSentryUser = ({ id, username }) => {
  if (currentUserId === id) {
    return;
  }

  Sentry.setUser({
    id,
    username
  });

  currentUserId = id;
};

export const removeSentryUser = () => {
  if (!currentUserId) {
    return;
  }

  Sentry.setUser(null);
  currentUserId = null;
};

/**
 * Creates helpers for sentry reporting
 *
 * @param {SentryTags} domain could be a module name to be tagged in sentry so that we can easily search the logs
 */
export function createSentryReport(domain) {
  /**
   * Set default context.
   * If domain is passed, all call to helpers below will be tagged in sentry.
   * There is also an option to set extra informations to sentry
   *
   * @see https://docs.sentry.io/platforms/javascript/#adding-context
   * @see https://docs.sentry.io/enriching-error-data/context/?platform=browser#tagging-events
   * @see https://docs.sentry.io/enriching-error-data/context/?platform=browser#extra-context
   */
  const setDefaultContext = (scope, extras) => {
    if (domain) {
      scope.setTag("domain", domain);
    }
    if (extras) {
      scope.setExtras(extras);
    }
  };

  const captureException = (e, extras) => {
    let eventId;
    Sentry.withScope((scope) => {
      scope.setLevel(Sentry.Severity.Error);
      setDefaultContext(scope, extras);
      eventId = Sentry.captureException(e);
    });
    return eventId;
  };

  const captureMessage = (severity, message, extras) => {
    let eventId;
    Sentry.withScope((scope) => {
      setDefaultContext(scope, extras);
      eventId = Sentry.captureMessage(message, severity);
    });
    return eventId;
  };

  return {
    captureDebugMessage: (message, extras) => {
      return captureMessage(Sentry.Severity.Debug, message, extras);
    },
    captureErrorMessage: (message, extras) => {
      return captureMessage(Sentry.Severity.Error, message, extras);
    },
    captureWarningMessage: (message, extras) => {
      return captureMessage(Sentry.Severity.Warning, message, extras);
    },
    captureException: (e, extras) => {
      return captureException(e, extras);
    },
    /**
     * Manually add breadcrumb for extra information to the report
     *
     * https://docs.sentry.io/enriching-error-data/breadcrumbs/?platform=browser#recording-crumbs
     */
    addBreadcrumb: (breadcrumb) => {
      Sentry.addBreadcrumb(breadcrumb);
    }
  };
}

export const SentryDomains = {
  CRASH: "crash"
};
